import logger from '@finn-no/fiaas-logger';
import type { NextPage } from 'next';
import { fetchVariants } from 'shared/experiment/variants';
import { getPageCounter } from 'shared/prometheus/prometheus';
import type { SspContext } from 'shared/types';
import { fetchOffers, fetchOffersForMap } from '@/api/fetcher';
import { MapResultPage } from '@/components/map/MapResultPage';
import { config, feConfig } from '@/config';
import { ExperimentProvider } from '@/hooks/experiments';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { runAllMiddlewares } from '@/server/middleware';
import { actions as filterActions } from '@/state/filterActions';
import { actions as mapResultActions } from '@/state/mapResultActions';
import { actions as resultActions } from '@/state/resultActions';
import { wrapper } from '@/state/store';

type MapPageProps = {
    hasLegalBasis: boolean;
    unleash: Record<string, boolean> | null;
    config: typeof feConfig;
    experiments: { [key: string]: string };
};

const MapPage: NextPage<MapPageProps> = (props: MapPageProps) => {
    wrapper.useHydration(props);

    return (
        <ExperimentProvider experiments={props.experiments}>
            <MapResultPage />
        </ExperimentProvider>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }: SspContext) => {
    if (query.skipSSRData) {
        return { props: { hasLegalBasis: false, unleash: null, config: feConfig, experiments: {} } };
    }

    await runAllMiddlewares(req, res, query);

    const experiments =
        (await fetchVariants(req, res, 'travel-pale-pp-web', config.get('experimentsApiUrl'), [
            'travel-prepackage-clickable_result_item_v2',
        ])) ?? {};

    const filters = browserQueryParamsToFilters(query);
    store.dispatch(filterActions.filter(filters));

    try {
        const data = await fetchOffers(config.get('pp.apiUrlInternal'), filtersToApiQueryParams(filters));
        store.dispatch(resultActions.setResults(data));

        const pois = await fetchOffersForMap(config.get('pp.apiUrlInternal'), filtersToApiQueryParams(filters));
        store.dispatch(mapResultActions.setMapResults(pois));
    } catch (error) {
        store.dispatch(resultActions.setError());
        store.dispatch(mapResultActions.setError());
        logger.error(`Failed to fetch offers for map page: ${error}`);
    }

    getPageCounter().inc({ app: 'pale-pp-web', page: 'map' });

    return {
        props: {
            hasLegalBasis: res.locals?.hasLegalBasis ?? false,
            unleash: res.locals?.unleash ?? null,
            config: feConfig,
            experiments,
        },
    };
});

export default MapPage;
